import { useCallback, useEffect } from 'react';

// Third-party
import Card from '@mui/material/Card';
import Paper from '@mui/material/Paper';
import CardHeader from '@mui/material/CardHeader';
import Box from '@mui/material/Box';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import moment from 'moment/moment';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material';

// Redux-toolkit
import { MemoizedBaseAvatar } from '@mgm/common/components/base';
import {
  getDailySubmissions,
  getSelectedDailySubmission,
} from '../../../../store/tasklists/selectors';
import { tasklistsActions } from '../../../../store/tasklists/slice';

// Hooks
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';

// Types
import {
  TEmptySubmission, TSubmission, TSubmissionTask, TTasklist,
} from '../../../../types';

// Components
import { SubmissionListWrapper } from './SubmissionListWrapper';
import { LinearProgressWithLabel } from '../../../../components/LinearProgressWithLabel';

// Assets
import { ReactComponent as ViewIcon } from '../../../../static/icons/ic_view.svg';

const DailySubmissionsNav = () => {
  const DailySubmissions = useAppSelector(getDailySubmissions);
  const selectedDailySubmission = useAppSelector(getSelectedDailySubmission);
  const dispatch = useAppDispatch();
  const theme = useTheme();

  useEffect(() => {
    dispatch(tasklistsActions.setSelectedDailySubmission(DailySubmissions[0]));
  }, []);

  const getCompletedTask = useCallback((tasks: Array<TSubmissionTask>) => tasks?.filter((task) => task.isDone).length, []);

  const getProgressValue = (tasks: Array<TSubmissionTask>) => (getCompletedTask(tasks) / (tasks?.length || 0)) * 100;

  const setSelectedSubmissionTemplate = (submission: TSubmission) => {
    dispatch(tasklistsActions.setSelectedSubmission({} as TEmptySubmission));
    dispatch(tasklistsActions.setSelectedTasklistToSubmit({} as TTasklist));
    dispatch(tasklistsActions.setSelectedDailySubmission(submission));
    dispatch(tasklistsActions.setIsOpenAllSubmissionsNav(false));
  };

  return (
    <SubmissionListWrapper
      isSubmissionsEmpty={!DailySubmissions.length}
    >
      <Box>
        {DailySubmissions?.map((submission) => (
          <Card
            sx={{
              mt: 1,
              backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
              boxShadow: 'none',
              ':hover': {
                background: 'rgba(168, 85, 247, 0.16)',
                borderRadius: 2,
              },
              ...(submission.tasklistId === selectedDailySubmission.tasklistId && {
                background: 'rgba(168, 85, 247, 0.16)',
                borderRadius: 2,
              }),
            }}
            elevation={0}
            component={Paper}
            key={submission.id}
          >
            <CardHeader
              sx={{
                py: 0,
                '&& .MuiCardHeader-avatar': {
                  marginRight: 0,
                },
              }}
              avatar={(
                <MemoizedBaseAvatar
                  avatarFile={submission.avatarFile}
                  avatarName={submission.submitterName}
                />
               )}
              title={submission.tasklistName}
              titleTypographyProps={{
                variant: 'body1',
              }}
              action={(
                <Box marginTop={2} marginRight={2}>
                  <Box marginBottom={1} display="flex" justifyContent="end">
                    <SvgIcon
                      onClick={() => setSelectedSubmissionTemplate(submission)}
                      className="cursor-pointer"
                      component={ViewIcon}
                    />
                  </Box>
                  <Box>
                    <Typography
                      component="span"
                      sx={{
                        color: theme.palette.primary.main,
                        pr: 1,
                      }}
                      variant="caption"
                    >
                      Last submitted:
                    </Typography>
                    <Typography component="span" variant="caption">
                      {moment(submission.submittedAt).format('MMM Do YYYY HH:mm:ss')}
                    </Typography>
                  </Box>
                </Box>
                  )}
            />
            <Stack spacing={3} sx={{ px: 3, mb: 2, mt: 1 }}>
              <LinearProgressWithLabel
                progress={getProgressValue(submission.tasks)}
                color={getProgressValue(submission.tasks) < 100 ? 'warning' : 'success'}
                sx={{ height: 8, bgcolor: 'grey.50016', borderRadius: 8 }}
              />
            </Stack>
          </Card>
        ))}
      </Box>
    </SubmissionListWrapper>
  );
};

export {
  DailySubmissionsNav,
};
